import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import Logo from "@redotech/redo-web/logo.svg";
import { Header, Text } from "@redotech/redo-web/text";
import { ClientError } from "@redotech/rpc/errors";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useRedoReferralsRpcClient } from "../rpc/client-context";
import { useAuthToken } from "./auth-token-context";
import { LoginHeroPanel } from "./login-hero-panel";
import * as loginCss from "./login.module.css";
import {
  messageForVerifyError,
  VERIFY_ERROR_FALLBACK,
} from "./verify-error-messages";

type State =
  | { kind: "idle" }
  | { kind: "verifying" }
  | { kind: "success" }
  | { kind: "error"; message: string };

function AuthIntro({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Flex align="center" dir="column" gap="xs">
      <Header
        as="h1"
        fontSize="xxs"
        fontWeight="semibold"
        m="none"
        textAlign="center"
      >
        {title}
      </Header>
      <Text
        fontSize="sm"
        fontWeight="thin"
        m="none"
        textAlign="center"
        textColor="tertiary"
      >
        {subtitle}
      </Text>
    </Flex>
  );
}

export const VerifyPage = memo(function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const rpcClient = useRedoReferralsRpcClient();
  const queryClient = useQueryClient();
  const { setToken } = useAuthToken();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "idle" });

  // Magic links are single-use; skip the StrictMode remount's second POST.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: VERIFY_ERROR_FALLBACK });
      return;
    }
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    setState({ kind: "verifying" });
    const controller = new AbortController();
    (async () => {
      try {
        const result = await rpcClient.verifyMagicLink(
          { token },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) {
          return;
        }
        setToken(result.authToken);
        queryClient.setQueryData(["me", result.authToken], result.referrer);
        void import("../dashboard");
        setState({ kind: "success" });
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (err instanceof ClientError) {
          setState({ kind: "error", message: messageForVerifyError(err.code) });
        } else {
          setState({ kind: "error", message: VERIFY_ERROR_FALLBACK });
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex className={loginCss.container} gap="none" w="full">
      <Flex
        bgColor="primary"
        className={loginCss.leftPanel}
        dir="column"
        grow={1}
      >
        <Flex pt="3xl" px="3xl">
          <a
            className={loginCss.logoLockup}
            href="https://www.redo.com"
            rel="noreferrer"
            target="_blank"
          >
            <Logo className={loginCss.logo} />
            <Text
              as="span"
              fontSize="lg"
              fontWeight="regular"
              m="none"
              textColor="primary"
            >
              Referrals
            </Text>
          </a>
        </Flex>
        <Flex
          align="center"
          className={loginCss.formWrapper}
          grow={1}
          justify="center"
          pb="6xl"
        >
          <Flex
            as="section"
            className={loginCss.formArea}
            dir="column"
            gap="3xl"
            w="full"
          >
            {(state.kind === "idle" || state.kind === "verifying") && (
              <>
                <AuthIntro
                  subtitle="Verifying your login link..."
                  title="Signing you in"
                />
                <Flex align="center" justify="center" py="4xl">
                  <div className={loginCss.loadingSpinner} />
                </Flex>
              </>
            )}
            {state.kind === "success" && (
              <AuthIntro subtitle="Redirecting..." title="Signed in" />
            )}
            {state.kind === "error" && (
              <>
                <AuthIntro
                  subtitle={state.message}
                  title="We couldn't sign you in"
                />
                <Flex dir="column" gap="lg">
                  <RedoButton
                    buttonClassName={loginCss.primaryActionButton}
                    className={loginCss.fullWidthButton}
                    hierarchy="primary"
                    onClick={() => navigate("/login", { replace: true })}
                    size="sm"
                    text="Request a new login link"
                    type="button"
                  />
                  <RedoButton
                    hierarchy="link"
                    IconLeading={ArrowLeftIcon}
                    onClick={() => navigate("/login", { replace: true })}
                    size="sm"
                    text="Back to sign in"
                  />
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
      <LoginHeroPanel />
    </Flex>
  );
});
